import { useEffect, useState } from "react";
import { api, formatApiError, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Ticket } from "lucide-react";

const blank = { code: "", type: "percent", value: 10, expiry_date: "", max_uses: "", active: true };

export default function AdminDiscounts() {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/discounts").then(({ data }) => setList(data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const remove = async (d) => {
    if (!window.confirm(`Delete discount code ${d.code}?`)) return;
    try { await api.delete(`/admin/discounts/${d.id}`); toast.success("Discount deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (list === null) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[#ff2a5f]" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono-x text-xs uppercase tracking-widest text-zinc-500">Promotions</div>
          <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white sm:text-3xl">Discount codes</h1>
        </div>
        <Button data-testid="new-discount" onClick={() => setEditing({ ...blank })} className="bg-[#ff2a5f] font-semibold text-white hover:bg-[#e01f50]">
          <Plus className="mr-1.5 h-4 w-4" /> New code
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-zinc-800 bg-[#0d0d10]">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-zinc-400">Code</TableHead>
              <TableHead className="text-zinc-400">Discount</TableHead>
              <TableHead className="text-zinc-400">Expiry</TableHead>
              <TableHead className="text-zinc-400">Usage</TableHead>
              <TableHead className="text-zinc-400">Status</TableHead>
              <TableHead className="text-right text-zinc-400">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.length === 0 ? (
              <TableRow className="border-zinc-800"><TableCell colSpan={6} className="py-12 text-center text-zinc-500">No discount codes yet. Create one to offer class or early-bird discounts.</TableCell></TableRow>
            ) : list.map((d) => (
              <TableRow key={d.id} data-testid={`discount-row-${d.id}`} className="border-zinc-800">
                <TableCell className="font-mono-x font-bold text-white">{d.code}</TableCell>
                <TableCell className="text-zinc-300">{d.type === "fixed" ? `${money(d.value)} off` : `${d.value}% off`}</TableCell>
                <TableCell className="text-zinc-400">{d.expiry_date || "—"}</TableCell>
                <TableCell className="text-zinc-400">{d.uses || 0}{d.max_uses ? ` / ${d.max_uses}` : ""}</TableCell>
                <TableCell>
                  <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${d.active ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-400" : "border-zinc-700 bg-zinc-800 text-zinc-500"}`}>
                    {d.active ? "Active" : "Inactive"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button data-testid={`edit-discount-${d.id}`} onClick={() => setEditing({ ...d, max_uses: d.max_uses ?? "", expiry_date: d.expiry_date || "" })} size="sm" variant="outline" className="h-8 border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800"><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button data-testid={`delete-discount-${d.id}`} onClick={() => remove(d)} size="sm" variant="outline" className="h-8 border-zinc-700 bg-transparent text-rose-400 hover:bg-zinc-800"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {editing && <DiscountDialog initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function DiscountDialog({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.code.trim()) return toast.error("Enter a code");
    if (!(Number(f.value) > 0)) return toast.error("Enter a value greater than 0");
    setBusy(true);
    const payload = {
      code: f.code.trim().toUpperCase(), type: f.type, value: Number(f.value),
      expiry_date: f.expiry_date || null, max_uses: f.max_uses === "" ? null : Number(f.max_uses), active: f.active,
    };
    try {
      if (f.id) await api.put(`/admin/discounts/${f.id}`, payload);
      else await api.post("/admin/discounts", payload);
      toast.success("Discount saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-zinc-800 bg-zinc-900 text-white sm:max-w-md">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-display"><Ticket className="h-5 w-5 text-[#ff2a5f]" /> {f.id ? "Edit" : "New"} discount code</DialogTitle>
          <DialogDescription className="text-zinc-500">Set the code, discount type and optional limits.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-zinc-300">Code</Label>
            <Input data-testid="discount-code" value={f.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="EARLYBIRD" className="mt-1 border-zinc-700 bg-zinc-950 font-mono-x text-white" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-300">Type</Label>
              <Select value={f.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="discount-type" className="mt-1 border-zinc-700 bg-zinc-950 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900 text-white">
                  <SelectItem value="percent">Percentage (%)</SelectItem>
                  <SelectItem value="fixed">Fixed amount (NZD)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300">{f.type === "fixed" ? "Amount (NZD)" : "Percent (%)"}</Label>
              <Input data-testid="discount-value" type="number" step="0.01" value={f.value} onChange={(e) => set("value", e.target.value)} className="mt-1 border-zinc-700 bg-zinc-950 text-white" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-300">Expiry date</Label>
              <Input data-testid="discount-expiry" type="date" value={f.expiry_date} onChange={(e) => set("expiry_date", e.target.value)} className="mt-1 border-zinc-700 bg-zinc-950 text-white" />
            </div>
            <div>
              <Label className="text-zinc-300">Max uses</Label>
              <Input data-testid="discount-maxuses" type="number" value={f.max_uses} onChange={(e) => set("max_uses", e.target.value)} placeholder="Unlimited" className="mt-1 border-zinc-700 bg-zinc-950 text-white" />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-zinc-800 p-3">
            <div><Label className="text-zinc-300">Active</Label><p className="text-xs text-zinc-500">Customers can only use active codes</p></div>
            <Switch data-testid="discount-active" checked={f.active} onCheckedChange={(c) => set("active", c)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400">Cancel</Button>
          <Button data-testid="save-discount" onClick={save} disabled={busy} className="bg-[#ff2a5f] text-white hover:bg-[#e01f50]">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
